import { useHasDocumentsInlineViewer } from '@framework/common-web/ui/documents/useHasDocumentsInlineViewer.tsx';
import { useAuthDownload } from '@framework/common-web/utils/download/useAuthDownload.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { getNetworkErrorMessage } from '@framework/ui/core/errors/getNetworkErrorMessage.tsx';
import { Button, message, Tooltip } from 'antd';
import { useCallback } from 'react';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { useTheme } from 'styled-components';
import { getDocumentoConsolidatoOmissisQueryOptions } from './getDocumentoConsolidatoOmissisQueryOptions.ts';
import { useButtonResponsiveProps } from './useButtonResponsiveProps.ts';

/**
 * Utility che torna `true` se il pulsante di download con omissis è abilitato
 * da variabile d'ambiente.
 */
function isEnabled() {
  return process.env.ENABLE_OMISSIS_PDF_DOWNLOAD_BUTTON === 'true';
}

/**
 * Controlla il messaggio d'errore per identificare gli errori
 * dovuti al fatto che la richiesta non è pubblica (e quindi non
 * ha un PDF con omissis).
 */
function isNotPublicError(errorMessage: string | null | undefined) {
  if (!errorMessage) return false;
  return errorMessage.includes('Richiesta non pubblica');
}

export interface IDownloadOmissisSubmissionButtonProps {
  disabled?: boolean;
  submissionId: string;
  submissionModuleId?: string | null;
  /** Mostra sempre l'icona */
  forceIcon?: boolean;
  /** Permette di forzare la visualizzazione desktop */
  ignoreMobile?: boolean;
  /** Pulsante di download in linea */
  inline?: boolean;
}

/**
 * Pulsante che permette il download con omissis di una richiesta lato backoffice.
 * 1. Per ora il pulsante scarica l'intera richiesta e non il singolo modulo
 *    come per `DownloadSubmissionModuleButton`.
 * 2. Lo mostriamo sempre, in caso di errore sovrascriviamo il messaggio con
 *    "Impossibile scaricare il PDF con Omissis: la Richiesta non è pubblica"
 */
export function DownloadOmissisSubmissionButton(
  props: IDownloadOmissisSubmissionButtonProps,
) {
  const {
    disabled,
    submissionId,
    forceIcon,
    ignoreMobile,
    inline,
    submissionModuleId,
  } = props;

  const theme = useTheme();
  const hasInlineViewer = useHasDocumentsInlineViewer();

  const isMobile = useBreakpoint(down('md'));
  const buttonText = hasInlineViewer
    ? t(
        'submission-module-button.visualizza-pdf-omissis',
        'Visualizza PDF Omissis',
      )
    : t('submission-module-button.scarica-pdf-omissis', 'Scarica PDF Omissis');

  const buttonResponsiveProps = useButtonResponsiveProps({
    buttonText,
    forceIcon,
    hasInlineViewer,
    ignoreMobile,
    isMobile,
    inline,
  });

  const { download, isFetching } = useAuthDownload(
    getDocumentoConsolidatoOmissisQueryOptions,
    [submissionId, submissionModuleId!],
    {
      onError: e => {
        // biome-ignore format: <tbd>
        console.error(`[DownloadSubmissionModuleButton] Error: `, { error: e });

        // biome-ignore format: <tbd>
        const errorMessage = getNetworkErrorMessage(e, t("submission-module-button.errore-in-download",'Si è verificato un problema durante il download del file'));

        // In caso di errore dovuto a una richiesta non pubblica, mostriamo un messaggio specifico
        // e non quello generico di errore di rete
        if (isNotPublicError(errorMessage)) {
          message.error(
            t(
              'submission-module-button.richiesta-non-pubblica',
              'Impossibile scaricare il PDF con Omissis: la Richiesta non è pubblica',
            ),
          );
        } else {
          message.error(errorMessage);
        }
      },
      showInlineViewer: hasInlineViewer,
      openInNewWindow: theme.openModulePdfInNewWindow,
    },
  );

  const handleClick = useCallback(() => {
    if (submissionId && !disabled) {
      download();
    }
  }, [download, disabled, submissionId]);

  // Se il pulsante è disabilitato, non lo mostriamo
  if (!isEnabled()) {
    return null;
  }

  return (
    <Tooltip
      title={
        disabled
          ? t(
              'form.salva-modulo-download-pdf',
              'Salva il modulo almeno una volta per scaricare il documento PDF',
            )
          : undefined
      }
    >
      <Button
        {...buttonResponsiveProps}
        onClick={handleClick}
        disabled={!submissionId || disabled}
        loading={isFetching}
        block={((isMobile && !ignoreMobile) ?? props.inline) ? undefined : true}
        size="large"
        type={props.inline ? 'link' : 'default'}
        // icon={props.inline ? <DownloadOutlined aria-hidden /> : undefined}
      />
    </Tooltip>
  );
}
